import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { crearEstudiante, registrarEstudiantesMasivo } from "../../../services/estudiante.service";
import { crearPersona } from "../../../services/persona.service";
import { crearEleccionIdioma } from "../../../services/eleccionIdioma";
import { crearInscripcion } from "../../../services/inscripcion.service";
import { crearRegistro } from "../../../services/registro.service";
import { getAllPaises } from "../../../services/pais.service";
import { getAllDepartamentosByIdPais } from "../../../services/departamento.service";
import { getAllProvinciasByIdDepartamento } from "../../../services/provincia.service";
import { getAllIdiomas } from "../../../services/idioma.service";
import { getAllGrupos } from "../../../services/grupo.service";
import { getAllNivelesByIdIdioma } from "../../../services/nivel.service";
import { getAllModulosByIdNivelAndIdIdioma } from "../../../services/modulo.service";
import { getAllProgramasByIdIdioma } from "../../../services/programa.service";
import { getAllTipoAdmisiones } from "../../../services/tipoAdmision.service";
import { showAlert, showErrorAlert } from "../../../components/molecules/ShowAlert";

export const RegistroMasivoEstudiantesPage = () => {
  const [estudiantes, setEstudiantes] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [cargandoDatos, setCargandoDatos] = useState(true);
  const fileInputRef = useRef(null);
  // Archivo Excel seleccionado
  const [archivoExcel, setArchivoExcel] = useState(null);

  // Estados para los datos de referencia
  const [paises, setPaises] = useState([]);
  // Eliminado: estados no utilizados (departamentos, provincias)
  const [idiomas, setIdiomas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [departamentosCat, setDepartamentosCat] = useState([]);
  // Quitado: validación cruzada de módulos (para evitar 404 y enfocarnos en otros campos)
  const [programasDisponibles, setProgramasDisponibles] = useState([]); // {idPrograma,nombre,idIdioma}
  
  const [tipoAdmisiones, setTipoAdmisiones] = useState([]);

  // Helpers de normalización/formatos
  const normalizar = (s) =>
    (s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  
  // Normalización más agresiva para búsquedas (sin espacios, solo letras y números)
  const normalizarAgresivo = (s) =>
    normalizar(s).replace(/[^a-z0-9]/g, '');

  const toStringSafe = (v) => v === null || v === undefined ? '' : String(v);

  const toISODate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const sexoToBackend = (s) => {
    const v = normalizar(s);
    if (v.startsWith("f")) return "F";
    if (v.startsWith("m")) return "M";
    return s || "";
  };

  // Función para cargar datos de referencia
  const cargarDatosReferencia = async () => {
    try {
      setCargandoDatos(true);
      console.log('🔄 Iniciando carga de datos de referencia...');
      
      // Cargar todos los datos en paralelo con mejor manejo de errores
      const [
        paisesData,
        idiomasData,
        tipoAdmisionesData,
        gruposData
      ] = await Promise.all([
        getAllPaises().catch(err => {
          console.error('❌ Error cargando países:', err);
          showErrorAlert({ text: 'Error cargando países. Verifica la conexión.' });
          return [];
        }),
        getAllIdiomas().catch(err => {
          console.error('❌ Error cargando idiomas:', err);
          showErrorAlert({ text: 'Error cargando idiomas. Verifica la conexión.' });
          return [];
        }),
        getAllTipoAdmisiones().catch(err => {
          console.error('❌ Error cargando tipos de admisión:', err);
          showErrorAlert({ text: 'Error cargando tipos de admisión. Verifica la conexión.' });
          return [];
        }),
        getAllGrupos().catch(err => {
          console.error('❌ Error cargando grupos:', err);
          showErrorAlert({ text: 'Error cargando grupos. Verifica la conexión.' });
          return [];
        })
      ]);

      // Validar que los datos no estén vacíos
      if (!paisesData || paisesData.length === 0) {
        console.warn('⚠️ No se cargaron países');
        showErrorAlert({ text: 'No se pudieron cargar los países. Verifica la base de datos.' });
      }
      
      if (!idiomasData || idiomasData.length === 0) {
        console.warn('⚠️ No se cargaron idiomas');
        showErrorAlert({ text: 'No se pudieron cargar los idiomas. Verifica la base de datos.' });
      }
      
      if (!tipoAdmisionesData || tipoAdmisionesData.length === 0) {
        console.warn('⚠️ No se cargaron tipos de admisión');
        showErrorAlert({ text: 'No se pudieron cargar los tipos de admisión. Verifica la base de datos.' });
      }

      setPaises(paisesData || []);
      setIdiomas(idiomasData || []);
      setTipoAdmisiones(tipoAdmisionesData || []);
      setGrupos(gruposData || []);

      // Cargar catálogo de departamentos de TODOS los países para validar nombres
      try {
        const todosDepartamentos = [];
        for (const pais of (paisesData || [])) {
          try {
            const dptos = await getAllDepartamentosByIdPais(pais.idPais).catch(() => []);
            if (dptos && dptos.length > 0) {
              todosDepartamentos.push(...dptos);
            }
          } catch (err) {
            console.warn(`Error cargando departamentos de ${pais.nombre}:`, err);
          }
        }
        setDepartamentosCat(todosDepartamentos);
        console.log(`✅ Cargados ${todosDepartamentos.length} departamentos de todos los países`);
      } catch (err) {
        console.error('Error cargando departamentos:', err);
        setDepartamentosCat([]);
      }

      // Precarga de Programas por idioma
      let programasCargados = [];
      try {
        const programasChunks = await Promise.all((idiomasData || []).map(async (idioma) => {
          const progs = await getAllProgramasByIdIdioma(idioma.idIdioma).catch(() => []);
          return progs.map(p => ({ ...p, idIdioma: idioma.idIdioma }));
        }));
        programasCargados = programasChunks.flat();
        setProgramasDisponibles(programasCargados);
      } catch (err) {
        console.error('Error cargando programas:', err);
        programasCargados = [];
      }

      console.log('✅ Datos cargados:', {
        paises: paisesData?.length || 0,
        idiomas: idiomasData?.length || 0,
        tipoAdmisiones: tipoAdmisionesData?.length || 0,
        grupos: gruposData?.length || 0,
        departamentos: departamentosCat?.length || 0,
        programas: programasCargados.length,
      });
      
      // Log detallado de programas cargados para debug
      if (programasCargados.length > 0) {
        const programasPorIdioma = {};
        programasCargados.forEach(p => {
          const idiomaNombre = idiomasData?.find(i => i.idIdioma === p.idIdioma)?.nombre || 'Desconocido';
          if (!programasPorIdioma[idiomaNombre]) {
            programasPorIdioma[idiomaNombre] = [];
          }
          programasPorIdioma[idiomaNombre].push(p.nombre);
        });
        console.log('📚 Programas cargados por idioma:', programasPorIdioma);
      }
      
      setCargandoDatos(false);
    } catch (error) {
      console.error('❌ Error general cargando datos de referencia:', error);
      showErrorAlert({ text: 'Error general cargando datos. Verifica la conexión y la consola.' });
      setCargandoDatos(false);
    }
  };

  // Función para mostrar nombres disponibles
  const mostrarNombresDisponibles = async () => {
    try {
      let info = "📋 **NOMBRES DISPONIBLES EN TU SISTEMA:**\n\n";
      
      // Países
      info += "🌍 **PAÍSES:**\n";
      paises.forEach(p => info += `- ${p.nombre}\n`);
      
      // Idiomas
      info += "\n🗣️ **IDIOMAS:**\n";
      idiomas.forEach(i => info += `- ${i.nombre}\n`);
      
      // Tipos de Admisión
      info += "\n🎓 **TIPOS DE ADMISIÓN:**\n";
      tipoAdmisiones.forEach(t => info += `- ${t.nombre}\n`);
      
      // Si tienes Bolivia, cargar departamentos
      const bolivia = paises.find(p => p.nombre.toLowerCase().includes('bolivia'));
      if (bolivia) {
        const departamentosBolivia = await getAllDepartamentosByIdPais(bolivia.idPais);
        info += "\n🏛️ **DEPARTAMENTOS DE BOLIVIA:**\n";
        departamentosBolivia.forEach(d => info += `- ${d.nombre}\n`);
        
        // Si tienes Santa Cruz, cargar provincias
        const santaCruz = departamentosBolivia.find(d => d.nombre.toLowerCase().includes('santa cruz'));
        if (santaCruz) {
          const provinciasSantaCruz = await getAllProvinciasByIdDepartamento(santaCruz.idDepartamento);
          info += "\n🏘️ **PROVINCIAS DE SANTA CRUZ:**\n";
          provinciasSantaCruz.forEach(p => info += `- ${p.nombre}\n`);
        }
      }
      
      // Si tienes algún idioma, cargar niveles (sin detallar módulos para evitar errores ruidosos)
      if (idiomas.length > 0) {
        const primerIdioma = idiomas[0];
        const nivelesIdioma = await getAllNivelesByIdIdioma(primerIdioma.idIdioma).catch(() => []);
        info += `\n📚 **NIVELES DE ${primerIdioma.nombre.toUpperCase()}:**\n`;
        nivelesIdioma.forEach(n => info += `- ${n.nombre}\n`);

        // Programas por idioma
        const programasIdioma = await getAllProgramasByIdIdioma(primerIdioma.idIdioma);
        if (programasIdioma.length > 0) {
          info += `\n🎯 **PROGRAMAS DE ${primerIdioma.nombre.toUpperCase()}:**\n`;
          programasIdioma.forEach(p => info += `- ${p.nombre}\n`);
        }
      }
      
      // Mostrar en consola y alerta
      console.log(info);
      showAlert({ 
        text: "Nombres disponibles mostrados en consola. Presiona F12 para verlos.",
        icon: 'info'
      });
      
    } catch (error) {
      console.error('Error mostrando nombres disponibles:', error);
      showErrorAlert({ text: 'Error al cargar nombres disponibles' });
    }
  };

  // Función para mostrar nombres disponibles con más detalle
  const mostrarNombresDisponiblesDetallado = async () => {
    try {
      let info = "🔍 **ANÁLISIS DETALLADO DE NOMBRES:**\n\n";
      
      // Países
      info += "🌍 **PAÍSES:**\n";
      paises.forEach(p => info += `- ${p.nombre} (ID: ${p.idPais})\n`);
      
      // Idiomas
      info += "\n🗣️ **IDIOMAS:**\n";
      idiomas.forEach(i => info += `- ${i.nombre} (ID: ${i.idIdioma})\n`);
      
      // Tipos de Admisión
      info += "\n🎓 **TIPOS DE ADMISIÓN:**\n";
      tipoAdmisiones.forEach(t => info += `- ${t.nombre} (ID: ${t.idTipoAdmision})\n`);
      
      // Si tienes Bolivia, cargar departamentos
      const bolivia = paises.find(p => p.nombre.toLowerCase().includes('bolivia'));
      if (bolivia) {
        const departamentosBolivia = await getAllDepartamentosByIdPais(bolivia.idPais);
        info += "\n🏛️ **DEPARTAMENTOS DE BOLIVIA:**\n";
        departamentosBolivia.forEach(d => info += `- ${d.nombre} (ID: ${d.idDepartamento})\n`);
        
        // Si tienes Santa Cruz, cargar provincias
        const santaCruz = departamentosBolivia.find(d => d.nombre.toLowerCase().includes('santa cruz'));
        if (santaCruz) {
          const provinciasSantaCruz = await getAllProvinciasByIdDepartamento(santaCruz.idDepartamento);
          info += "\n🏘️ **PROVINCIAS DE SANTA CRUZ:**\n";
          provinciasSantaCruz.forEach(p => info += `- ${p.nombre} (ID: ${p.idProvincia})\n`);
        }
      }
      
      // Si tienes algún idioma, cargar niveles (omitimos módulos para evitar 404 ruidosos)
      if (idiomas.length > 0) {
        const primerIdioma = idiomas[0];
        const nivelesIdioma = await getAllNivelesByIdIdioma(primerIdioma.idIdioma).catch(() => []);
        info += `\n📚 **NIVELES DE ${primerIdioma.nombre.toUpperCase()}:**\n`;
        nivelesIdioma.forEach(n => info += `- ${n.nombre} (ID: ${n.idNivel})\n`);

        // Programas por idioma
        const programasIdioma = await getAllProgramasByIdIdioma(primerIdioma.idIdioma);
        if (programasIdioma.length > 0) {
          info += `\n🎯 **PROGRAMAS DE ${primerIdioma.nombre.toUpperCase()}:**\n`;
          programasIdioma.forEach(p => info += `- ${p.nombre} (ID: ${p.idPrograma})\n`);
        }
      }
      
      // Mostrar en consola y alerta
      console.log(info);
      showAlert({ 
        text: "Análisis detallado mostrado en consola. Presiona F12 para ver IDs y nombres exactos.",
        icon: 'info'
      });
      
    } catch (error) {
      console.error('Error mostrando nombres disponibles:', error);
      showErrorAlert({ text: 'Error al cargar nombres disponibles' });
    }
  };

  // Cargar datos al montar el componente
  React.useEffect(() => {
    cargarDatosReferencia();
  }, []);

  // Función para descargar plantilla Excel (xlsx) real, compatible con backend
  const descargarPlantilla = () => {
    const headers = [
      'Nombre','Apellido','Ci','Correo','Celular','Sexo','AnioAdmision','FechaNacimiento',
      'DepartamentoNombre','ProvinciaNombre','TipoAdmisionNombre','GrupoNombre','OrganizacionSocial',
      'Credito','ProgramaNombre','IdiomaNombre','ModuloNombre'
    ];
    const ejemplo1 = ['Juan','Pérez','12345678','juan@email.com','70012345','Masculino','2024-01-15','1990-01-01',
      'Santa Cruz','Andrés Ibáñez','Regular','','',0,'Soy Bilingüe 15 - Cruceño','Inglés','Módulo 1'];
    const ejemplo2 = ['María','García','87654321','maria@email.com','70054321','Femenino','2024-01-15','1992-05-15',
      'Santa Cruz','Andrés Ibáñez','Becario','','',4,'Soy Bilingüe 15 - Cruceño','Inglés','Módulo 1'];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ejemplo1, ejemplo2]);
    // Ajuste de ancho de columnas para legibilidad
    ws['!cols'] = headers.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'Plantilla_Estudiantes.xlsx');
  };

  // Parsear Excel para vista previa
  const procesarExcelParaVistaPrevia = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (!rows || rows.length <= 1) {
        setEstudiantes([]);
        return;
      }
      const headers = (rows[0] || []).map(h => String(h || '').trim().toLowerCase());
      const idx = (name) => headers.findIndex(h => h === name.toLowerCase());
      const iNombre = idx('nombre');
      const iApellido = idx('apellido');
      const iCI = idx('ci');
      const iCorreo = idx('correo');
      const iCelular = idx('celular');
      const iSexo = idx('sexo');
      const iAnio = idx('anioadmision');
      const iFechaNac = idx('fechanacimiento');
      const iDepto = idx('departamentonombre');
      const iProv = idx('provincianombre');
      const iTipoAdm = idx('tipoadmisionnombre');
      const iGrupo = idx('gruponombre');
      const iOrg = idx('organizacionsocial');
      const iCredito = idx('credito');
      const iPrograma = idx('programanombre');
      const iIdioma = idx('idiomanombre');
      const iModulo = idx('modulonombre');

      const estudiantesData = rows.slice(1).map((row) => {
        // Extraer valores originales del Excel
        const departamentoRaw = toStringSafe(row[iDepto]).trim();
        const provinciaRaw = toStringSafe(row[iProv]).trim();
        const tipoAdmisionRaw = toStringSafe(row[iTipoAdm]).trim();
        const idiomaRaw = toStringSafe(row[iIdioma]).trim();
        const moduloRaw = toStringSafe(row[iModulo]).trim();
        const programaRaw = toStringSafe(row[iPrograma]).trim();
        const grupoRaw = toStringSafe(row[iGrupo]).trim();

        // Obtener nombres exactos de la BD (importante para que coincidan con backend)
        const departamentoExacto = obtenerNombreExactoBD(departamentoRaw, departamentosCat);
        const tipoAdmisionExacto = obtenerNombreExactoBD(tipoAdmisionRaw, tipoAdmisiones);
        const idiomaExacto = obtenerNombreExactoBD(idiomaRaw, idiomas);
        const grupoExacto = obtenerNombreExactoBD(grupoRaw, grupos);
        const programaExacto = obtenerNombreExactoBD(programaRaw, programasDisponibles);

        return {
          nombre: toStringSafe(row[iNombre]).trim(),
          apellido: toStringSafe(row[iApellido]).trim(),
          ci: toStringSafe(row[iCI])?.trim() || null,
          correo: toStringSafe(row[iCorreo])?.trim() || null,
          celular: toStringSafe(row[iCelular]).trim(),
          sexo: toStringSafe(row[iSexo]).trim(),
          fechaNacimiento: toStringSafe(row[iFechaNac])?.trim() || null,
          anioAdmision: toStringSafe(row[iAnio])?.trim() || null,
          tipoAdmision: tipoAdmisionExacto,
          organizacionSocial: toStringSafe(row[iOrg])?.trim() || null,
          credito: Number.isNaN(parseInt(row[iCredito])) ? 0 : parseInt(row[iCredito]),
          departamento: departamentoExacto,
          provincia: provinciaRaw || null,
          idioma: idiomaExacto,
          modulo: moduloRaw,
          programa: programaExacto || null,
          grupoNombre: grupoExacto,
          // Guardar valores originales para referencia
          _departamentoOriginal: departamentoRaw,
          _provinciaOriginal: provinciaRaw
        };
      }).filter(est => toStringSafe(est.nombre) && toStringSafe(est.apellido));

      setEstudiantes(estudiantesData);
      showAlert({ text: `${estudiantesData.length} estudiantes detectados en el Excel` });
    } catch (err) {
      console.error('Error leyendo Excel:', err);
      showErrorAlert({ text: 'No se pudo leer el archivo Excel' });
    }
  };

  // Función para validar nombres antes de enviar al backend
  const validarNombresEnExcel = async () => {
    if (estudiantes.length === 0) return { valido: false, errores: ['No hay estudiantes para validar'] };
    
    const errores = [];
    const nombresUsados = {
      departamentos: new Set(),
      provincias: new Set(),
      tipoAdmisiones: new Set(),
      idiomas: new Set(),
      grupos: new Set(),
      programas: new Set()
    };
    
    for (const [idx, est] of estudiantes.entries()) {
      // Validar departamento
      const depNormalizado = normalizar(est.departamento);
      let depEncontrado = departamentosCat.find(d => normalizar(d.nombre) === depNormalizado);
      
      if (!depEncontrado) {
        depEncontrado = departamentosCat.find(d => {
          const nombreItem = normalizar(d.nombre || '');
          return nombreItem.includes(depNormalizado) || depNormalizado.includes(nombreItem);
        });
      }
      
      if (!depEncontrado) {
        errores.push(`Fila ${idx + 1}: Departamento "${est.departamento}" no existe`);
        nombresUsados.departamentos.add(est.departamento);
      } else {
        // Si el departamento existe, validar provincia (si está especificada)
        if (est.provincia?.trim()) {
          try {
            const provinciasDelDepto = await getAllProvinciasByIdDepartamento(depEncontrado.idDepartamento);
            const provNormalizada = normalizar(est.provincia);
            const provEncontrada = provinciasDelDepto.find(p => {
              const nombreProv = normalizar(p?.nombre || '');
              return nombreProv === provNormalizada || 
                     nombreProv.includes(provNormalizada) || 
                     provNormalizada.includes(nombreProv);
            });
            
            if (!provEncontrada) {
              errores.push(`Fila ${idx + 1}: Provincia "${est.provincia}" no existe en el departamento "${est.departamento}"`);
              nombresUsados.provincias.add(est.provincia);
            }
          } catch (err) {
            console.warn(`Error validando provincia para fila ${idx + 1}:`, err);
          }
        }
      }
      
      // Validar tipo admisión
      const tipoNormalizado = normalizar(est.tipoAdmision);
      const tipoEncontrado = tipoAdmisiones.find(t => normalizar(t.nombre) === tipoNormalizado);
      if (!tipoEncontrado) {
        errores.push(`Fila ${idx + 1}: Tipo de admisión "${est.tipoAdmision}" no existe`);
        nombresUsados.tipoAdmisiones.add(est.tipoAdmision);
      }
      
      // Validar idioma
      const idiomaNormalizado = normalizar(est.idioma);
      const idiomaEncontrado = idiomas.find(i => normalizar(i.nombre) === idiomaNormalizado);
      if (!idiomaEncontrado) {
        errores.push(`Fila ${idx + 1}: Idioma "${est.idioma}" no existe`);
        nombresUsados.idiomas.add(est.idioma);
      }
      
      // Validar grupo
      const grupoNormalizado = normalizar(est.grupoNombre);
      const grupoEncontrado = grupos.find(g => normalizar(g.nombre) === grupoNormalizado);
      if (!grupoEncontrado) {
        errores.push(`Fila ${idx + 1}: Grupo "${est.grupoNombre}" no existe`);
        nombresUsados.grupos.add(est.grupoNombre);
      }
    }
    
    // Construir mensaje de error más limpio
    if (errores.length > 0) {
      const mensajePrincipal = `Se encontraron ${errores.length} error(es) en el Excel:\n\n${errores.slice(0, 5).join('\n')}`;
      const mensajeExtra = errores.length > 5 ? `\n\n... y ${errores.length - 5} error(es) más. Revisa la tabla de vista previa para ver todos los errores.` : '';
      
      // Solo mostrar nombres disponibles si hay errores específicos de esos campos
      let mensajeNombres = '';
      if (nombresUsados.departamentos.size > 0 || nombresUsados.provincias.size > 0) {
        mensajeNombres += `\n\n📍 Revisa los nombres de Departamentos/Provincias en tu Excel.`;
      }
      if (nombresUsados.tipoAdmisiones.size > 0 || nombresUsados.idiomas.size > 0 || nombresUsados.grupos.size > 0) {
        mensajeNombres += `\n\n📋 Revisa los nombres de Tipo Admisión, Idioma y Grupo.`;
      }
      
      return {
        valido: false,
        errores: mensajePrincipal + mensajeExtra + mensajeNombres
      };
    }
    
    return {
      valido: true,
      errores: 'Sin errores'
    };
  };

  // Función para pre-procesar el Excel y reemplazar nombres por los exactos de la BD
  const preprocesarExcelConNombresExactos = async (file) => {
    try {
      // Leer el Excel
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      
      if (!rows || rows.length <= 1) {
        throw new Error('El Excel está vacío o no tiene datos');
      }
      
      // Obtener índices de columnas
      const headers = (rows[0] || []).map(h => String(h || '').trim().toLowerCase());
      const idx = (name) => headers.findIndex(h => h === name.toLowerCase());
      const iDepto = idx('departamentonombre');
      const iProv = idx('provincianombre');
      const iTipoAdm = idx('tipoadmisionnombre');
      const iIdioma = idx('idiomanombre');
      const iModulo = idx('modulonombre');
      const iPrograma = idx('programanombre');
      const iGrupo = idx('gruponombre');
      
      // Procesar cada fila (empezando desde la 1, no la 0 que es el header)
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        
        // Reemplazar nombres con los exactos de la BD
        if (iDepto >= 0 && row[iDepto]) {
          const nombreOriginal = String(row[iDepto] || '').trim();
          const nombreExacto = obtenerNombreExactoBD(nombreOriginal, departamentosCat);
          if (nombreExacto !== nombreOriginal) {
            row[iDepto] = nombreExacto;
            console.log(`📝 Fila ${i + 1}: Departamento "${nombreOriginal}" → "${nombreExacto}"`);
          }
        }
        
        if (iProv >= 0 && row[iProv]) {
          const nombreOriginal = String(row[iProv] || '').trim();
          // Buscar provincia después (necesitamos el departamento primero)
          // Por ahora dejamos el nombre original, se normalizará después si es necesario
        }
        
        if (iTipoAdm >= 0 && row[iTipoAdm]) {
          const nombreOriginal = String(row[iTipoAdm] || '').trim();
          const nombreExacto = obtenerNombreExactoBD(nombreOriginal, tipoAdmisiones);
          if (nombreExacto !== nombreOriginal) {
            row[iTipoAdm] = nombreExacto;
          }
        }
        
        if (iIdioma >= 0 && row[iIdioma]) {
          const nombreOriginal = String(row[iIdioma] || '').trim();
          const nombreExacto = obtenerNombreExactoBD(nombreOriginal, idiomas);
          if (nombreExacto !== nombreOriginal) {
            row[iIdioma] = nombreExacto;
          }
        }
        
        if (iGrupo >= 0 && row[iGrupo]) {
          const nombreOriginal = String(row[iGrupo] || '').trim();
          const nombreExacto = obtenerNombreExactoBD(nombreOriginal, grupos);
          if (nombreExacto !== nombreOriginal) {
            row[iGrupo] = nombreExacto;
          }
        }
        
        if (iPrograma >= 0 && row[iPrograma]) {
          const nombreOriginal = String(row[iPrograma] || '').trim();
          const nombreExacto = obtenerNombreExactoBD(nombreOriginal, programasDisponibles);
          if (nombreExacto !== nombreOriginal) {
            row[iPrograma] = nombreExacto;
          }
        }
      }
      
      // Crear nuevo workbook con los datos modificados
      const newWorkbook = XLSX.utils.book_new();
      const newSheet = XLSX.utils.aoa_to_sheet(rows);
      XLSX.utils.book_append_sheet(newWorkbook, newSheet, workbook.SheetNames[0]);
      
      // Convertir a blob
      const excelBuffer = XLSX.write(newWorkbook, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Crear nuevo File con el nombre original
      const nuevoArchivo = new File([blob], file.name, { type: file.type });
      
      return nuevoArchivo;
    } catch (error) {
      console.error('Error pre-procesando Excel:', error);
      throw error;
    }
  };

  // Subida directa del archivo Excel al backend
  const registrarEstudiantesMasivoUI = async () => {
    if (!archivoExcel) {
      showErrorAlert({ text: 'Selecciona un archivo Excel (.xlsx o .xls)' });
      return;
    }
    
    // Validar que se cargaron los datos de referencia
    if (!departamentosCat || departamentosCat.length === 0 || !tipoAdmisiones || tipoAdmisiones.length === 0 || !idiomas || idiomas.length === 0 || !grupos || grupos.length === 0) {
      showErrorAlert({ 
        text: 'Error: No se han cargado los datos de referencia. Por favor, espera a que se carguen los datos del sistema.' 
      });
      return;
    }
    
    // Validar nombres en el Excel antes de enviar
    setProcesando(true);
    const validacion = await validarNombresEnExcel();
    
    if (!validacion.valido) {
      setProcesando(false);
      showErrorAlert({ 
        text: `⚠️ ${validacion.errores}\n\nPor favor, corrige los errores en el Excel antes de continuar.` 
      });
      return;
    }
    
    try {
      // Pre-procesar Excel: reemplazar nombres con los exactos de la BD
      console.log('🔄 Pre-procesando Excel para normalizar nombres...');
      const archivoPreprocesado = await preprocesarExcelConNombresExactos(archivoExcel);
      console.log('✅ Excel pre-procesado, nombres normalizados');
      
      // Enviar el Excel pre-procesado al backend
      const res = await registrarEstudiantesMasivo(archivoPreprocesado);
      showAlert({ text: res?.Mensaje || 'Proceso completado', icon: 'success' });
      limpiarDatos();
    } catch (error) {
      console.error('Error en registro masivo:', error);
      
      // Extraer mensaje de error del backend
      let mensajeError = 'Error durante el registro masivo';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          mensajeError = errorData.message;
        } else if (typeof errorData === 'string') {
          mensajeError = errorData;
        } else if (errorData.errors) {
          mensajeError = JSON.stringify(errorData.errors);
        }
      } else if (error.message) {
        mensajeError = error.message;
      }
      
      // Limpiar mensaje de error - remover información redundante
      let mensajeLimpio = mensajeError;
      
      // Si el error menciona provincia o departamento, dar mensaje más claro
      if (mensajeError.includes('Provincia') || mensajeError.includes('Departamento')) {
        mensajeLimpio = mensajeError.split('Los nombres')[0].trim() || mensajeError;
        mensajeLimpio += '\n\n⚠️ Verifica que los nombres en el Excel coincidan EXACTAMENTE con los de la base de datos.';
        mensajeLimpio += '\n\n💡 Tip: Revisa la tabla de vista previa para ver qué campos tienen errores.';
      }
      
      showErrorAlert({ 
        text: `❌ ${mensajeLimpio}` 
      });
    } finally {
      setProcesando(false);
    }
  };

  // Función para buscar ID por nombre (tolerante)
  const buscarIdPorNombre = (nombre, lista, campoNombre = 'nombre') => {
    const target = normalizar(nombre);
    const encontrado = (lista || []).find(item => normalizar(item?.[campoNombre]) === target);
    return encontrado;
  };

  // Función para obtener el nombre exacto de la BD basado en búsqueda normalizada
  const obtenerNombreExactoBD = (nombreBuscar, lista, campoNombre = 'nombre') => {
    if (!nombreBuscar || !lista || lista.length === 0) return nombreBuscar;
    
    const nombreNormalizado = normalizar(nombreBuscar);
    
    // Primera búsqueda: coincidencia exacta normalizada
    const encontrado = lista.find(item => {
      const nombreItem = normalizar(item?.[campoNombre] || '');
      return nombreItem === nombreNormalizado;
    });
    
    if (encontrado) {
      return encontrado[campoNombre]; // Retornar el nombre exacto de la BD
    }
    
    // Segunda búsqueda: búsqueda parcial flexible
    const encontradoParcial = lista.find(item => {
      const nombreItem = normalizar(item?.[campoNombre] || '');
      return nombreItem.includes(nombreNormalizado) || 
             nombreNormalizado.includes(nombreItem);
    });
    
    if (encontradoParcial) {
      return encontradoParcial[campoNombre]; // Retornar el nombre exacto de la BD
    }
    
    // Si no se encuentra, retornar el nombre original
    return nombreBuscar;
  };

  // Función para validar estudiante
  const validarEstudiante = (estudiante) => {
    const errores = [];
    
    if (!toStringSafe(estudiante.nombre).trim()) errores.push('Nombre requerido');
    if (!toStringSafe(estudiante.apellido).trim()) errores.push('Apellido requerido');
    // CI y Correo son opcionales para carga masiva (según backend)
    if (!toStringSafe(estudiante.celular).trim()) {
      errores.push('Celular requerido');
    } else {
      const celularSoloDigitos = toStringSafe(estudiante.celular).replace(/\D/g, '');
      if (celularSoloDigitos.length < 8) errores.push('Celular inválido (mín 8 dígitos)');
    }

    // Sexo: debe ser Masculino/M, Femenino/F (evitar typos como 'Fmenino')
    if (!toStringSafe(estudiante.sexo).trim()) {
      errores.push('Sexo requerido');
    } else {
      const vs = normalizar(estudiante.sexo);
      const esValido = (vs === 'm' || vs === 'f' || vs.startsWith('masc') || vs.startsWith('feme'));
      if (!esValido) errores.push('Sexo inválido');
    }
    if (!toStringSafe(estudiante.tipoAdmision).trim()) {
      errores.push('Tipo de admisión requerido');
    } else {
      const tipoEncontrado = buscarIdPorNombre(estudiante.tipoAdmision, tipoAdmisiones);
      if (!tipoEncontrado) errores.push(`Tipo de admisión inválido: "${estudiante.tipoAdmision}"`);
    }
    if (!toStringSafe(estudiante.departamento).trim()) errores.push('Departamento requerido');
    else {
      // Normalizar nombre del departamento antes de buscar
      const nombreDepartamentoOriginal = toStringSafe(estudiante.departamento).trim();
      const nombreDepartamentoNormalizado = normalizar(nombreDepartamentoOriginal);
      
      // Buscar coincidencia exacta normalizada
      let dpto = (departamentosCat || []).find(item => {
        const nombreItem = normalizar(item?.nombre || '');
        return nombreItem === nombreDepartamentoNormalizado;
      });
      
      if (!dpto) {
        // Intentar búsqueda más flexible (coincidencias parciales)
        dpto = (departamentosCat || []).find(item => {
          const nombreItem = normalizar(item?.nombre || '');
          return nombreItem.includes(nombreDepartamentoNormalizado) || 
                 nombreDepartamentoNormalizado.includes(nombreItem);
        });
      }
      
      if (!dpto) {
        // Intentar búsqueda sin normalización (comparación directa con trim)
        dpto = (departamentosCat || []).find(item => {
          const nombreItem = toStringSafe(item?.nombre || '').trim();
          return nombreItem.toLowerCase() === nombreDepartamentoOriginal.toLowerCase();
        });
      }
      
      if (!dpto) {
        // Búsqueda agresiva (sin caracteres especiales)
        const depAgresivo = normalizarAgresivo(nombreDepartamentoOriginal);
        dpto = (departamentosCat || []).find(item => {
          const nombreItem = normalizarAgresivo(item?.nombre || '');
          return nombreItem === depAgresivo;
        });
      }
      
      if (!dpto) {
        errores.push(`Departamento no existe: "${estudiante.departamento}"`);
      }
    }
    if (!toStringSafe(estudiante.idioma).trim()) {
      errores.push('Idioma requerido');
    } else {
      const idiomaEncontrado = buscarIdPorNombre(estudiante.idioma, idiomas);
      if (!idiomaEncontrado) errores.push(`Idioma inválido: "${estudiante.idioma}"`);
    }

    if (!toStringSafe(estudiante.modulo).trim()) {
      errores.push('Módulo requerido');
    }
    if (!toStringSafe(estudiante.grupoNombre).trim()) {
      errores.push('Grupo requerido');
    } else {
      const grupoEncontrado = buscarIdPorNombre(estudiante.grupoNombre, grupos);
      if (!grupoEncontrado) {
        errores.push(`Grupo no existe: "${estudiante.grupoNombre}"`);
      }
    }

    // Validar programa si está especificado (para todos los tipos, no solo becarios)
    // IMPORTANTE: Buscamos el programa en TODOS los idiomas, no solo en el especificado
    // porque puede estar mal asociado en la BD pero el programa existe
    if (toStringSafe(estudiante.programa).trim()) {
      const programaNombreNormalizado = normalizar(estudiante.programa);
      const programaNombreAgresivo = normalizarAgresivo(estudiante.programa);
      
      // PRIMERO: Buscar en TODOS los programas disponibles (sin importar idioma)
      let programaEncontradoFront = programasDisponibles.find(p => {
        const nombrePrograma = normalizar(p?.nombre || '');
        return nombrePrograma === programaNombreNormalizado;
      });
      
      // Si no encuentra exacto, buscar con normalización agresiva
      if (!programaEncontradoFront) {
        programaEncontradoFront = programasDisponibles.find(p => {
          const nombrePrograma = normalizarAgresivo(p?.nombre || '');
          return nombrePrograma === programaNombreAgresivo;
        });
      }
      
      // Si aún no encuentra, búsqueda parcial flexible
      if (!programaEncontradoFront) {
        programaEncontradoFront = programasDisponibles.find(p => {
          const nombrePrograma = normalizar(p?.nombre || '');
          return nombrePrograma.includes(programaNombreNormalizado) || 
                 programaNombreNormalizado.includes(nombrePrograma);
        });
      }
      
      if (programaEncontradoFront) {
        // Programa encontrado - verificar si está en el idioma correcto
        const idiomaEncontrado = buscarIdPorNombre(estudiante.idioma, idiomas);
        if (idiomaEncontrado && programaEncontradoFront.idIdioma !== idiomaEncontrado.idIdioma) {
          const idiomaPrograma = idiomas.find(i => i.idIdioma === programaEncontradoFront.idIdioma);
          console.warn(`⚠️ Programa "${estudiante.programa}" encontrado pero está asociado al idioma "${idiomaPrograma?.nombre || 'Desconocido'}" en lugar de "${estudiante.idioma}". Continuando de todas formas.`);
        }
        // NO agregamos error - el programa existe, permitimos que continúe
      } else {
        // Programa no encontrado en absoluto - mostrar error simple
        const idiomaEncontrado = buscarIdPorNombre(estudiante.idioma, idiomas);
        if (idiomaEncontrado) {
          const progsIdioma = programasDisponibles.filter(p => p.idIdioma === idiomaEncontrado.idIdioma);
          if (progsIdioma.length > 0) {
            console.warn(`Programa "${estudiante.programa}" no encontrado en ningún idioma. Programas del idioma "${estudiante.idioma}" disponibles:`, progsIdioma.map(p => p.nombre).join(', '));
            errores.push(`Programa inválido: "${estudiante.programa}" no existe en el idioma "${estudiante.idioma}"`);
          } else {
            // No hay programas para ese idioma - no validar
            console.warn(`No se cargaron programas para el idioma "${estudiante.idioma}"`);
          }
        } else {
          errores.push(`Programa inválido: "${estudiante.programa}" no existe`);
        }
      }
    }
    
    // Reglas específicas para Becario
    if (normalizar(estudiante.tipoAdmision) === 'becario') {
      if (!toStringSafe(estudiante.programa).trim()) errores.push('Programa requerido para becario');
      const creditoVal = parseInt(estudiante.credito);
      if (!(creditoVal > 0)) errores.push('Crédito > 0 requerido para becario');
    }
    
    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (toStringSafe(estudiante.correo) && !emailRegex.test(toStringSafe(estudiante.correo))) {
      errores.push('Formato de correo inválido');
    }
    
    // Validar formato de fecha (opcionales)
    if (toStringSafe(estudiante.fechaNacimiento) && isNaN(Date.parse(toStringSafe(estudiante.fechaNacimiento)))) {
      errores.push('Formato de fecha inválido');
    }
    if (toStringSafe(estudiante.anioAdmision) && isNaN(Date.parse(toStringSafe(estudiante.anioAdmision)))) {
      errores.push('Formato de año admisión inválido');
    }

    return {
      valido: errores.length === 0,
      errores
    };
  };

  // Función para registrar estudiantes masivamente
  const registrarEstudiantes = async () => {
    if (estudiantes.length === 0) {
      showErrorAlert({ text: 'No hay estudiantes para registrar' });
      return;
    }

    // Validar todos los estudiantes
    const validaciones = estudiantes.map(est => ({
      ...est,
      ...validarEstudiante(est)
    }));

    const estudiantesInvalidos = validaciones.filter(v => !v.valido);
    const estudiantesValidos = validaciones.filter(v => v.valido);
    if (estudiantesInvalidos.length > 0) {
      showAlert({
        text: `Se procesarán ${estudiantesValidos.length} válidos. Se omiten ${estudiantesInvalidos.length} con errores.`,
        icon: 'warning'
      });
    }

    setProcesando(true);
    setProgreso(0);

    try {
      let exitosos = 0;
      let fallidos = 0;

      for (let i = 0; i < estudiantesValidos.length; i++) {
        const estudiante = estudiantesValidos[i];
        
        try {
          // Buscar IDs por nombres
          // País: no viene en Excel. Asumimos Bolivia por defecto (o primero disponible)
          const paisEncontrado = paises.find(p => normalizar(p.nombre) === 'bolivia') || paises[0];
          const tipoAdmisionEncontrado = buscarIdPorNombre(estudiante.tipoAdmision, tipoAdmisiones);
          const idiomaEncontrado = buscarIdPorNombre(estudiante.idioma, idiomas);

          if (!paisEncontrado) {
            throw new Error(`País no disponible en sistema`);
          }
          if (!tipoAdmisionEncontrado) {
            throw new Error(`Tipo de admisión no encontrado: ${estudiante.tipoAdmision}`);
          }
          if (!idiomaEncontrado) {
            throw new Error(`Idioma no encontrado: ${estudiante.idioma}`);
          }

          // Buscar departamento con normalización mejorada
          const departamentosDelPais = await getAllDepartamentosByIdPais(paisEncontrado.idPais);
          const nombreDepartamentoNormalizado = normalizar(estudiante.departamento);
          let departamentoEncontrado = departamentosDelPais.find(d => {
            const nombreD = normalizar(d?.nombre || '');
            return nombreD === nombreDepartamentoNormalizado;
          });

          if (!departamentoEncontrado) {
            // Intentar búsqueda más flexible
            const departamentoAlternativo = departamentosDelPais.find(d => {
              const nombreD = normalizar(d?.nombre || '');
              return nombreD.includes(nombreDepartamentoNormalizado) || 
                     nombreDepartamentoNormalizado.includes(nombreD);
            });
            
            if (!departamentoAlternativo) {
              throw new Error(`Departamento "${estudiante.departamento}" no encontrado`);
            }
            
            departamentoEncontrado = departamentoAlternativo;
          }

          // Buscar provincia (opcional) con normalización mejorada
          let provinciaEncontrada = null;
          if (estudiante.provincia?.trim()) {
            const provinciasDelDepartamento = await getAllProvinciasByIdDepartamento(departamentoEncontrado.idDepartamento);
            const nombreProvinciaNormalizado = normalizar(estudiante.provincia);
            provinciaEncontrada = provinciasDelDepartamento.find(p => {
              const nombreP = normalizar(p?.nombre || '');
              return nombreP === nombreProvinciaNormalizado;
            });
            
            if (!provinciaEncontrada) {
              // Intentar búsqueda más flexible
              const provinciaAlternativa = provinciasDelDepartamento.find(p => {
                const nombreP = normalizar(p?.nombre || '');
                return nombreP.includes(nombreProvinciaNormalizado) || 
                       nombreProvinciaNormalizado.includes(nombreP);
              });
              
              if (provinciaAlternativa) {
                provinciaEncontrada = provinciaAlternativa;
              } else {
                console.warn(`Provincia "${estudiante.provincia}" no encontrada, continuando sin provincia`);
              }
            }
          }

          // Resolver módulo sin requerir columna de nivel: buscar en todos los niveles del idioma
          const nivelesDelIdioma = await getAllNivelesByIdIdioma(idiomaEncontrado.idIdioma);
          let moduloEncontrado = null;
          for (const nivel of nivelesDelIdioma) {
            const modulosDelNivel = await getAllModulosByIdNivelAndIdIdioma(nivel.idNivel, idiomaEncontrado.idIdioma);
            const m = buscarIdPorNombre(estudiante.modulo, modulosDelNivel);
            if (m) {
              moduloEncontrado = m;
              break;
            }
          }

          if (!moduloEncontrado) {
            throw new Error(`Módulo no encontrado: ${estudiante.modulo} en ${estudiante.idioma}`);
          }

          // Buscar programa (opcional, solo para becarios)
          let programaEncontrado = null;
          if (estudiante.programa?.trim()) {
            const programasDelIdioma = await getAllProgramasByIdIdioma(idiomaEncontrado.idIdioma);
            programaEncontrado = buscarIdPorNombre(estudiante.programa, programasDelIdioma);
            if (!programaEncontrado) {
              throw new Error(`Programa no encontrado: ${estudiante.programa} en ${estudiante.idioma}`);
            }
          }

          // Validar grupo por nombre exacto con base
          const grupoExacto = buscarIdPorNombre(estudiante.grupoNombre, grupos);
          if (!grupoExacto) {
            throw new Error(`Grupo no encontrado en sistema: ${estudiante.grupoNombre}`);
          }

          // 1. Crear persona primero
          const datosPersona = {
            nombre: estudiante.nombre.trim(),
            apellido: estudiante.apellido.trim(),
            ci: estudiante.ci?.trim() || null,
            correo: estudiante.correo?.trim() || null,
            celular: estudiante.celular?.trim() || null,
            sexo: sexoToBackend(estudiante.sexo),
            fechaNacimiento: toISODate(estudiante.fechaNacimiento) || null,
            anioAdmision: toISODate(estudiante.anioAdmision) || null,
            idDepartamento: departamentoEncontrado.idDepartamento,
            idProvincia: provinciaEncontrada?.idProvincia || null
          };

          const personaCreada = await crearPersona(datosPersona);
          
          // 2. Crear estudiante
          const datosEstudiante = {
            idPersona: personaCreada.idPersona,
            idTipoAdmision: tipoAdmisionEncontrado.idTipoAdmision,
            organizacionSocial: estudiante.organizacionSocial?.trim() || null,
            credito: Number.isNaN(parseInt(estudiante.credito)) ? null : parseInt(estudiante.credito)
          };

          const estudianteCreado = await crearEstudiante(datosEstudiante);
          
          // 3. Crear inscripción (siempre) y usarla en elección de idioma
          const datosInscripcion = {
            idEstudiante: estudianteCreado.idEstudiante,
            idPrograma: programaEncontrado?.idPrograma || null
          };
          const inscripcionCreada = await crearInscripcion(datosInscripcion);

          // 4. Crear elección de idioma con idInscripcion (sin nivel)
          const datosEleccionIdioma = {
            idInscripcion: inscripcionCreada.idInscripcion,
            idIdioma: idiomaEncontrado.idIdioma,
            idModulo: moduloEncontrado.idModulo
          };
          await crearEleccionIdioma(datosEleccionIdioma);

          // 5. Crear registro asignando explícitamente el grupo exacto
          try {
            await crearRegistro({
              fechaRegistro: new Date().toISOString(),
              estado: "INSCRITO",
              notaFinal: 0,
              totalPrecio: 0,
              idGrupo: grupoExacto.idGrupo,
              idEstudiante: estudianteCreado.idEstudiante,
              participacionBloque1: 0,
              writingBloque1: 0,
              speakingBloque1: 0,
              tareasBloque1: 0,
              examenBloque1: 0,
              promedioBloque1: 0,
              participacionBloque2: 0,
              writingBloque2: 0,
              speakingBloque2: 0,
              tareasBloque2: 0,
              examenBloque2: 0,
              promedioBloque2: 0,
              puntosExtras: 0
            });
          } catch (e) {
            console.warn('No se pudo crear registro de grupo para el estudiante', estudianteCreado.idEstudiante, e);
          }

          exitosos++;
                 } catch (error) {
           console.error(`Error registrando estudiante ${i + 1}:`, error);
           
           // Mostrar error específico en consola
           if (error.response?.data) {
             console.error(`Respuesta del backend:`, error.response.data);
           }
           
           fallidos++;
         }

        // Actualizar progreso
        setProgreso(((i + 1) / estudiantesValidos.length) * 100);
      }

      // Mostrar resultados
      if (fallidos === 0 && estudiantesInvalidos.length === 0) {
        showAlert({ 
          text: `✅ ${exitosos} estudiantes registrados exitosamente`,
          icon: 'success'
        });
        setEstudiantes([]);
        setArchivoSeleccionado(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        showAlert({ 
          text: `✅ ${exitosos} exitosos, ❌ ${fallidos} fallidos, ⏭️ ${estudiantesInvalidos.length} omitidos por validación`,
          icon: 'warning'
        });
      }
    } catch (error) {
      console.error('Error en registro masivo:', error);
      showErrorAlert({ text: 'Error durante el registro masivo' });
    } finally {
      setProcesando(false);
      setProgreso(0);
    }
  };

  // Función para limpiar datos
  const limpiarDatos = () => {
    setEstudiantes([]);
    setArchivoSeleccionado(null);
    setProgreso(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <ContenedorPageTemplate titulo="Registro Masivo de Estudiantes">
      <div className="max-w-6xl mx-auto">
        
          {/* Sección de Plantilla */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 rounded-2xl p-8 mb-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-2xl font-bold text-blue-800 mb-3">Plantilla Excel</h3>
                             <p className="text-blue-700 text-lg max-w-2xl mx-auto">
                 Descarga la plantilla Excel, llénala con los datos de los estudiantes y súbela para el registro masivo.
               </p>
            </div>
            
            <div className="flex gap-4 flex-wrap justify-center">
               <button
                 onClick={descargarPlantilla}
                 disabled={cargandoDatos}
                 className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-4 rounded-xl shadow-lg transition-all duration-300 flex items-center gap-3 text-lg font-semibold transform hover:scale-105 disabled:transform-none"
               >
                 {cargandoDatos ? '🔄 Cargando...' : '📥 Descargar Plantilla Excel'}
               </button>
             </div>
                  {cargandoDatos && (
                <div className="text-center mt-6">
                  <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-6 py-3 rounded-full border border-blue-200">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-sm font-semibold">🔄 Cargando datos de referencia del sistema...</span>
                  </div>
                  <p className="text-blue-600 text-sm mt-2">Esto puede tomar unos segundos</p>
                </div>
              )}
              
              {!cargandoDatos && (paises.length === 0 || idiomas.length === 0 || tipoAdmisiones.length === 0) && (
                <div className="text-center mt-6">
                  <div className="inline-flex items-center gap-3 bg-gradient-to-r from-red-100 to-pink-100 text-red-800 px-6 py-3 rounded-full border border-red-200">
                    <span className="text-lg">⚠️</span>
                    <span className="text-sm font-semibold">Algunos datos no se cargaron correctamente</span>
                  </div>
                  <p className="text-red-600 text-sm mt-2">Verifica la consola para más detalles</p>
                  <button
                    onClick={cargarDatosReferencia}
                    className="mt-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
                  >
                    🔄 Reintentar Carga
                  </button>
                </div>
              )}
              
              {!cargandoDatos && paises.length > 0 && idiomas.length > 0 && tipoAdmisiones.length > 0 && (
                <div className="text-center mt-6">
                  <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-6 py-3 rounded-full border border-green-200">
                    <span className="text-lg">✅</span>
                    <span className="text-sm font-semibold">Datos cargados correctamente</span>
                  </div>
                  <p className="text-green-600 text-sm mt-2">
                    {paises.length} países, {idiomas.length} idiomas, {tipoAdmisiones.length} tipos de admisión
                  </p>
                </div>
              )}
          </div>

          {/* Sección de Subida de Archivo */}
         <div className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 rounded-2xl p-8 mb-8 shadow-xl">
           <div className="text-center mb-6">
             <div className="text-6xl mb-4">📁</div>
             <h3 className="text-2xl font-bold text-green-800 mb-3">Subir Archivo Excel</h3>
             <p className="text-green-700 text-lg">
               Selecciona tu archivo Excel lleno con los datos de los estudiantes
             </p>
           </div>          
           <div className="max-w-2xl mx-auto">
             <div className="relative">
               <input
                 ref={fileInputRef}
                 type="file"
                 accept=".xlsx,.xls"
                 onChange={(e) => {
                   const archivo = e.target.files[0];
                    if (archivo) {
                      setArchivoExcel(archivo);
                      setArchivoSeleccionado(archivo.name);
                      // Generar vista previa
                      procesarExcelParaVistaPrevia(archivo);
                    }
                 }}
                 className="w-full border-2 border-dashed border-green-300 rounded-xl px-6 py-8 text-center text-green-600 hover:border-green-400 transition-colors cursor-pointer bg-white/50 hover:bg-white/80"
               />
               <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                 <div className="text-center">
                   <div className="text-4xl mb-2">📤</div>
                   <p className="text-green-600 font-medium">Haz clic para seleccionar archivo Excel</p>
                   <p className="text-green-500 text-sm">o arrastra y suelta aquí</p>
                 </div>
               </div>
             </div>
             
             {archivoSeleccionado && (
               <div className="mt-4 text-center">
                 <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                   <span className="text-2xl">✅</span>
                   <span className="font-medium">{archivoSeleccionado}</span>
                 </div>
               </div>
             )}
           </div>
         </div>

          {/* Vista Previa de Estudiantes */}
         {estudiantes.length > 0 && (
           <div className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200 rounded-2xl p-8 mb-8 shadow-xl">
                           <div className="flex flex-row justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                  <div className="text-4xl">👥</div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-800">
                      Vista Previa de Estudiantes
                    </h3>
                    <p className="text-blue-600">
                      {estudiantes.length} estudiante{estudiantes.length !== 1 ? 's' : ''} detectado{estudiantes.length !== 1 ? 's' : ''} en el Excel
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={limpiarDatos}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-xl shadow-lg transition-all duration-300 flex items-center gap-2 text-sm font-semibold transform hover:scale-105 whitespace-nowrap"
                  >
                    🗑️ Limpiar
                  </button>
                  <button
                    onClick={registrarEstudiantesMasivoUI}
                    disabled={procesando}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-2 rounded-xl shadow-lg transition-all duration-300 flex items-center gap-2 text-sm font-semibold transform hover:scale-105 disabled:transform-none whitespace-nowrap"
                  >
                    {procesando ? '🔄 Procesando...' : '🚀 Registrar Estudiantes'}
                  </button>
                </div>
              </div>

             {/* Barra de Progreso */}
             {procesando && (
               <div className="mb-8 bg-white rounded-2xl p-8 shadow-lg border border-blue-100">
                 <div className="text-center mb-4">
                   <h4 className="text-xl font-semibold text-blue-800 mb-2">Progreso del Registro</h4>
                   <div className="text-3xl font-bold text-blue-600 mb-4">{Math.round(progreso)}%</div>
                 </div>
                 
                 <div className="w-full bg-blue-100 rounded-full h-4 mb-4">
                   <div 
                     className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all duration-700 ease-out shadow-lg" 
                     style={{ width: `${progreso}%` }}
                   ></div>
                 </div>
                 
                 <div className="text-center">
                   <p className="text-lg text-blue-700 font-medium">
                     {Math.round(progreso * estudiantes.length / 100)} de {estudiantes.length} estudiantes procesados
                   </p>
                   <p className="text-sm text-blue-600 mt-2">
                     {procesando ? '🔄 Procesando...' : '✅ Completado'}
                   </p>
                 </div>
               </div>
             )}

                                                       

             {/* Tabla de Estudiantes Mejorada */}
             <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-100">
               <div className="overflow-x-auto">
                 <table className="min-w-full">
                   <thead className="bg-gradient-to-r from-blue-500 to-blue-600">
                     <tr>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">#</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Nombre</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Apellido</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Celular</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Sexo</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">CI</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Correo</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Departamento</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Provincia</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Tipo Admisión</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Grupo</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Org. Social</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Crédito</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Idioma</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Módulo</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Programa</th>
                       <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Estado</th>
                     </tr>
                   </thead>
                   <tbody className="bg-white divide-y divide-blue-100">
                     {estudiantes.map((estudiante, index) => {
                       const validacion = validarEstudiante(estudiante);
                       const errorGrupo = validacion.errores?.find(e => e.toLowerCase().startsWith('grupo'));
                       // Muestra múltiples errores concatenados para mayor claridad
                       const erroresVisibles = (errorGrupo ? [errorGrupo, ...validacion.errores.filter(e => e !== errorGrupo)] : validacion.errores).slice(0, 3);
                       const textoErrores = erroresVisibles.join(' | ');
                       return (
                         <tr key={index} className={`hover:bg-blue-50 transition-all duration-200 ${validacion.valido ? 'bg-green-50' : 'bg-red-50'}`}>
                           <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">{index + 1}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{estudiante.nombre}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{estudiante.apellido}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.celular}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.sexo}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">{estudiante.ci}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">{estudiante.correo}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.departamento}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.provincia}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.tipoAdmision}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.grupoNombre}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.organizacionSocial}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.credito}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.idioma}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.modulo}</td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{estudiante.programa || 'Regular'}</td>
                           <td className="px-6 py-4 whitespace-nowrap">
                            {validacion.valido ? (
                              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-green-100 text-green-800 border border-green-200">
                                ✅ Válido
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-red-100 text-red-800 border border-red-200" title={validacion.errores.join(', ')}>
                                ❌ {textoErrores}
                              </span>
                            )}
                          </td>
                         </tr>
                       );
                     })}
                   </tbody>
                 </table>
               </div>
             </div>
           </div>
         )}

                                   
      </div>
    </ContenedorPageTemplate>
  );
};
